// Analytics and tracking utilities
import { getGAMeasurementId, getGtmId, getMetaPixelId } from './obfuscate';

interface AnalyticsEvent {
  event: string;
  category?: string;
  action?: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

interface VehicleData {
  id: string;
  brand: string;
  model: string;
  version?: string;
  year: string;
  price: number;
  mileage?: number;
}

interface LeadData {
  name: string;
  email: string;
  phone: string;
  message?: string;
  serviceType?: string;
  vehicle?: VehicleData;
}

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    google_tag_manager?: Record<string, unknown>;
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}

/**
 * Initialize Google Tag Manager
 */
export const initGoogleTagManager = (gtmId: string) => {
  if (!gtmId || typeof window === 'undefined') return;

  const validGtmId = String(gtmId).trim();
  if (!validGtmId || !validGtmId.startsWith('GTM-')) return;

  // Avoid double initialization
  if (document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${validGtmId}"]`)) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${validGtmId}`;
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
};

/**
 * Initialize Google Analytics
 */
export const initGoogleAnalytics = (measurementId: string) => {
  if (!measurementId || typeof window === 'undefined') return;

  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer?.push(arguments);
  };
  
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: true,
    anonymize_ip: true
  });

};

/**
 * Initialize Meta Pixel (Facebook Pixel)
 */
export const initMetaPixel = (pixelId: string) => {
  // Validate pixelId before proceeding
  if (!pixelId || typeof window === 'undefined' || pixelId === 'null' || pixelId === 'undefined') return;
  
  // Ensure pixelId is a valid string (numbers only for Meta Pixel)
  const validPixelId = String(pixelId).trim();
  if (!validPixelId || validPixelId === '' || validPixelId === '1234567890') return;

  // Load Meta Pixel script
  (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  // Only initialize if we have a valid pixelId
  if (validPixelId && window.fbq) {
    window.fbq('init', validPixelId);
    window.fbq('track', 'PageView');
  }

};

/**
 * Track page view
 */
export const trackPageView = (path: string, title?: string) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }

};

/**
 * Track vehicle view
 */
export const trackVehicleView = (vehicle: VehicleData) => {
  const eventData = {
    event: 'view_item',
    ecommerce: {
      items: [{
        item_id: vehicle.id,
        item_name: `${vehicle.brand} ${vehicle.model} ${vehicle.version || ''}`.trim(),
        item_brand: vehicle.brand,
        item_category: 'Vehicle',
        price: vehicle.price,
        quantity: 1
      }]
    }
  };

  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'view_item', eventData.ecommerce);
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_ids: [vehicle.id],
      content_type: 'product',
      content_name: `${vehicle.brand} ${vehicle.model}`,
      value: vehicle.price,
      currency: 'BRL'
    });
  }

};

/**
 * Track lead submission (CONVERSÃO PRINCIPAL)
 */
export const trackLeadSubmission = (leadData: LeadData) => {
  const vehicle = leadData.vehicle;
  const serviceType = leadData.serviceType || 'general_inquiry';

  // Google Analytics - Lead event
  if (window.gtag) {
    window.gtag('event', 'generate_lead', {
      event_category: 'Lead',
      event_label: serviceType,
      value: vehicle?.price || 0,
      currency: 'BRL',
      vehicle_id: vehicle?.id,
      vehicle_brand: vehicle?.brand,
      vehicle_model: vehicle?.model,
      service_type: serviceType
    });

    // Also track as conversion
    window.gtag('event', 'conversion', {
      send_to: 'AW-CONVERSION_ID/CONVERSION_LABEL', // Replace with actual conversion ID
      value: vehicle?.price || 0,
      currency: 'BRL',
      transaction_id: `lead_${Date.now()}`
    });
  }

  // Meta Pixel - Lead event
  if (window.fbq) {
    window.fbq('track', 'Lead', {
      content_name: vehicle ? `${vehicle.brand} ${vehicle.model}` : serviceType,
      content_category: 'Lead Submission',
      value: vehicle?.price || 0,
      currency: 'BRL',
      vehicle_id: vehicle?.id,
      service_type: serviceType
    });
  }

};

/**
 * Track financing simulation
 */
export const trackFinancingSimulation = (vehicle: VehicleData) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'financing_simulation', {
      event_category: 'Service',
      event_label: 'Financing',
      vehicle_id: vehicle.id,
      vehicle_brand: vehicle.brand,
      vehicle_model: vehicle.model,
      value: vehicle.price,
      currency: 'BRL'
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('trackCustom', 'FinancingSimulation', {
      content_name: `${vehicle.brand} ${vehicle.model}`,
      value: vehicle.price,
      currency: 'BRL'
    });
  }

};

/**
 * Track WhatsApp click
 */
export const trackWhatsAppClick = (source: string, vehicle?: VehicleData) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'whatsapp_click', {
      event_category: 'Engagement',
      event_label: source,
      vehicle_id: vehicle?.id,
      vehicle_brand: vehicle?.brand,
      vehicle_model: vehicle?.model
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'Contact', {
      content_name: source,
      vehicle_id: vehicle?.id
    });
  }

};

/**
 * Track phone click
 */
export const trackPhoneClick = (source: string) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'phone_click', {
      event_category: 'Engagement',
      event_label: source
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('trackCustom', 'PhoneClick', {
      source: source
    });
  }

};

/**
 * Track service request
 */
export const trackServiceRequest = (serviceType: string, vehicle?: VehicleData) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'service_request', {
      event_category: 'Service',
      event_label: serviceType,
      vehicle_id: vehicle?.id,
      vehicle_brand: vehicle?.brand,
      vehicle_model: vehicle?.model,
      value: vehicle?.price || 0,
      currency: 'BRL'
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('trackCustom', 'ServiceRequest', {
      service_type: serviceType,
      vehicle_id: vehicle?.id,
      value: vehicle?.price || 0,
      currency: 'BRL'
    });
  }

};

/**
 * Track test drive request
 */
export const trackTestDriveRequest = (vehicle: VehicleData) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'test_drive_request', {
      event_category: 'Service',
      event_label: 'Test Drive',
      vehicle_id: vehicle.id,
      vehicle_brand: vehicle.brand,
      vehicle_model: vehicle.model,
      value: vehicle.price,
      currency: 'BRL'
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('trackCustom', 'TestDriveRequest', {
      content_name: `${vehicle.brand} ${vehicle.model}`,
      vehicle_id: vehicle.id,
      value: vehicle.price,
      currency: 'BRL'
    });
  }

};

/**
 * Track vehicle search
 */
export const trackVehicleSearch = (searchTerm: string, filters?: any) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'search', {
      search_term: searchTerm,
      ...filters
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'Search', {
      search_string: searchTerm,
      content_category: 'Vehicle'
    });
  }

};

/**
 * Track favorite (wishlist)
 */
export const trackAddToFavorites = (vehicle: VehicleData) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'add_to_wishlist', {
      event_category: 'Engagement',
      event_label: 'Add to Favorites',
      vehicle_id: vehicle.id,
      vehicle_brand: vehicle.brand,
      vehicle_model: vehicle.model,
      value: vehicle.price,
      currency: 'BRL'
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'AddToWishlist', {
      content_ids: [vehicle.id],
      content_name: `${vehicle.brand} ${vehicle.model}`,
      value: vehicle.price,
      currency: 'BRL'
    });
  }

};

/**
 * Track vehicle comparison
 */
export const trackVehicleComparison = (vehicles: VehicleData[]) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'compare_vehicles', {
      event_category: 'Engagement',
      event_label: 'Vehicle Comparison',
      num_vehicles: vehicles.length,
      vehicle_ids: vehicles.map(v => v.id).join(',')
    });
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('trackCustom', 'CompareVehicles', {
      num_vehicles: vehicles.length,
      content_ids: vehicles.map(v => v.id)
    });
  }

};

/**
 * Custom event tracker
 */
export const trackCustomEvent = (eventName: string, eventData?: any) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', eventName, eventData);
  }

  // Meta Pixel
  if (window.fbq) {
    window.fbq('trackCustom', eventName, eventData);
  }

};

/**
 * Initialize all analytics based on environment variables
 */
export const initializeAnalytics = (config?: any) => {
  // Get analytics IDs from environment variables (accessed in obfuscated way)
  const gtmId = getGtmId();
  const gaMeasurementId = getGAMeasurementId();
  const metaPixelId = getMetaPixelId();

  // Initialize Google Tag Manager first (shares dataLayer with GA)
  if (gtmId && gtmId !== 'GTM-XXXXXXX' && gtmId !== 'null' && gtmId !== 'undefined') {
    initGoogleTagManager(gtmId);
  }

  // Initialize Google Analytics if measurement ID is provided
  if (gaMeasurementId && gaMeasurementId !== 'G-XXXXXXXXXX') {
    initGoogleAnalytics(gaMeasurementId);
  }

  // Initialize Meta Pixel if pixel ID is provided and valid
  if (metaPixelId && metaPixelId !== '1234567890' && metaPixelId !== 'null' && metaPixelId !== 'undefined') {
    initMetaPixel(metaPixelId);
  }

};

